import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Grid,
  Typography,
  Card,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import Button from "components/CustomButtons/Button.js";
import { colors } from '../components/Theme/WebTheme';
import { useTranslation } from "react-i18next";
import SubscriptionAPI from '../common/subscriptionApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  statsCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    minHeight: 120,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  statsNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: colors.MAIN_COLOR,
    marginBottom: theme.spacing(1),
  },
  statsLabel: {
    color: '#666',
    fontSize: '0.875rem',
  },
  chartCard: {
    padding: theme.spacing(2),
    minHeight: 300,
  },
  chartTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    color: '#333',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 400,
  },
  errorContainer: {
    padding: theme.spacing(2),
  },
  renewalItem: {
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  renewalDate: {
    color: '#ff9800',
    fontSize: '0.875rem',
    fontWeight: 'bold',
  },
  pieChartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 250,
  },
  legendContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
}));

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export default function SubscriptionStatistics() {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalSubscribers: 0,
    activeSubscribers: 0,
    pendingSubscribers: 0,
    expiredSubscribers: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    planDistribution: {},
    userTypeDistribution: { customer: 0, driver: 0 },
    upcomingRenewals: []
  });
  const [allPlans, setAllPlans] = useState([]); // State untuk menyimpan semua plans dari API
  const [plansLoading, setPlansLoading] = useState(false); // Loading state untuk plans

  const currentLanguage = i18n.language || 'en';

  // Load all plans for plan name resolution
  const loadAllPlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      const data = await SubscriptionAPI.getSubscriptionPlans(
        null,
        currentLanguage
      );
      if (data.success) {
        // Convert plans object to array format
        const plansArray = Object.entries(data.plans || {}).map(
          ([key, plan]) => ({
            id: key,
            name: plan.translations?.[data.language]?.name || plan.name,
            price: plan.price,
            period: plan.period,
            userType: plan.userType || "customer",
          })
        );
        setAllPlans(plansArray);
        console.log("📋 Loaded plans for SubscriptionStatistics:", plansArray);
      }
    } catch (error) {
      console.error("Error loading plans for statistics:", error);
    } finally {
      setPlansLoading(false);
    }
  }, [currentLanguage]);

  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await SubscriptionAPI.getSubscriptionStats();

      if (response.success && response.stats) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError(t('error_loading_statistics'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadStatistics();
    loadAllPlans(); // Load plans for plan name resolution
  }, [currentLanguage, loadStatistics, loadAllPlans]); // Load when language changes

  useEffect(() => {
    // Auto-refresh every 30 seconds - separate effect to avoid memory leak
    const interval = setInterval(loadStatistics, 30000);

    return () => clearInterval(interval);
  }, [loadStatistics]); // No dependencies - only set up once


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(currentLanguage, {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString(currentLanguage);
  };

  // Helper function untuk membersihkan nama plan - sama seperti di SubscriptionSubscribers.js
  const formatPlanName = (planName) => {
    if (!planName) return t("unknown_plan");
    
    // Jika nama plan diawali dengan 'plan_', cari nama asli dari data subscription plans
    if (planName.startsWith('plan_')) {
      const matchingPlan = allPlans.find(plan => plan.id === planName);
      if (matchingPlan && matchingPlan.name) {
        console.log(`📋 Found matching plan in stats: ${planName} -> ${matchingPlan.name}`);
        return matchingPlan.name;
      }
      
      // Fallback jika tidak ditemukan di allPlans: tampilkan sebagai "Plan" dengan ID pendek
      if (planName.length > 20) {
        const shortId = planName.split('_')[1]?.substring(0, 8) || 'unknown';
        return `${t("plan")} ${shortId}`;
      }
    }
    
    // Jika nama plan mengandung karakter aneh atau terlalu panjang, bersihkan
    if (planName.length > 50) {
      return planName.substring(0, 50) + '...';
    }
    
    // Bersihkan nama plan dari karakter khusus yang tidak perlu
    return planName.replace(/[^\w\s\-]/g, '').trim() || t("unnamed_plan");
  };

  // Prepare data for charts
  const planDistributionData = Object.keys(stats.planDistribution).map(planId => ({
    name: formatPlanName(planId), // Use formatPlanName to get proper plan name
    value: stats.planDistribution[planId],
    planId: planId
  }));

  const userTypeData = [
    { name: t('customers'), value: stats.userTypeDistribution.customer },
    { name: t('drivers'), value: stats.userTypeDistribution.driver }
  ];

  const statusDistributionData = [
    { name: t('active'), value: stats.activeSubscribers },
    { name: t('pending'), value: stats.pendingSubscribers },
    { name: t('expired'), value: stats.expiredSubscribers },
    { name: t('cancelled'), value: stats.cancelledSubscribers || 0 }
  ].filter(item => item.value > 0);

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null; // Don't show label for slices less than 5%
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" style={{ fontWeight: 'bold' }}>
          {t('subscription_statistics')}
        </Typography>
        <Button
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={loadStatistics}
          disabled={loading}
        >
          {t('refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" className={classes.errorContainer}>
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <div className={classes.statsNumber}>
              {stats.totalSubscribers}
            </div>
            <Typography className={classes.statsLabel}>
              {t('total_subscribers')}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <div className={classes.statsNumber} style={{ color: '#4CAF50' }}>
              {stats.activeSubscribers}
            </div>
            <Typography className={classes.statsLabel}>
              {t('active_subscribers')}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <div className={classes.statsNumber} style={{ color: '#2196F3' }}>
              {formatCurrency(stats.monthlyRevenue)}
            </div>
            <Typography className={classes.statsLabel}>
              {t('monthly_revenue')}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statsCard}>
            <div className={classes.statsNumber} style={{ color: '#FF9800' }}>
              {stats.upcomingRenewals.length}
            </div>
            <Typography className={classes.statsLabel}>
              {t('upcoming_renewals')}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Plan Distribution */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {t('plan_distribution')}
            </Typography>
            {planDistributionData.length > 0 ? (
              <>
                <div className={classes.pieChartContainer}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={planDistributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {planDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, t('subscribers')]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className={classes.legendContainer}>
                  {planDistributionData.map((entry, index) => (
                    <div key={entry.planId} className={classes.legendItem}>
                      <div
                        className={classes.legendColor}
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <Typography variant="caption">
                        {entry.name} ({entry.value})
                      </Typography>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', paddingTop: 50 }}>
                {t('no_data_available')}
              </Typography>
            )}
          </Card>
        </Grid>

        {/* User Type Distribution */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {t('user_type_distribution')}
            </Typography>
            {userTypeData.some(item => item.value > 0) ? (
              <>
                <div className={classes.pieChartContainer}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={userTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {userTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, t('subscribers')]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className={classes.legendContainer}>
                  {userTypeData.map((entry, index) => (
                    <div key={entry.name} className={classes.legendItem}>
                      <div
                        className={classes.legendColor}
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <Typography variant="caption">
                        {entry.name} ({entry.value})
                      </Typography>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', paddingTop: 50 }}>
                {t('no_data_available')}
              </Typography>
            )}
          </Card>
        </Grid>

        {/* Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {t('subscription_status_distribution')}
            </Typography>
            {statusDistributionData.some(item => item.value > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={statusDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [value, t('subscribers')]} />
                  <Bar dataKey="value" fill={colors.MAIN_COLOR} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', paddingTop: 50 }}>
                {t('no_data_available')}
              </Typography>
            )}
          </Card>
        </Grid>

        {/* Upcoming Renewals */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {t('upcoming_renewals')} ({stats.upcomingRenewals.length})
            </Typography>
            {stats.upcomingRenewals.length > 0 ? (
              <List style={{ maxHeight: 220, overflow: 'auto' }}>
                {stats.upcomingRenewals.map((renewal, index) => (
                  <ListItem key={index} className={classes.renewalItem}>
                    <Avatar style={{ marginRight: 12, width: 32, height: 32 }}>
                      {renewal.userId?.charAt(0) || 'U'}
                    </Avatar>
                    <ListItemText
                      primary={formatPlanName(renewal.planName)}
                      secondary={
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            {t('user_id')}: {renewal.userId}
                          </Typography>
                          <br />
                          <Typography className={classes.renewalDate}>
                            {t('due')}: {formatDate(renewal.endDate)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', paddingTop: 50 }}>
                {t('no_upcoming_renewals')}
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Summary */}
      <Grid container spacing={3} style={{ marginTop: 16 }}>
        <Grid item xs={12}>
          <Card style={{ padding: 24 }}>
            <Typography variant="h6" className={classes.chartTitle}>
              {t('revenue_summary')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h4" style={{ color: colors.MAIN_COLOR, fontWeight: 'bold' }}>
                    {formatCurrency(stats.totalRevenue)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('total_revenue')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h4" style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    {formatCurrency(stats.monthlyRevenue)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('this_month')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h4" style={{ color: '#FF9800', fontWeight: 'bold' }}>
                    {stats.totalRevenue > 0 ?
                      formatCurrency(stats.totalRevenue / Math.max(stats.totalSubscribers, 1)) :
                      formatCurrency(0)
                    }
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('average_per_subscriber')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
} 